import React, { useState, useEffect } from 'react';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import universalNotificationService from '../services/universalNotificationService';
import { toast } from 'react-hot-toast';


interface PushNotificationManagerProps {
  className?: string;
}

const PushNotificationManager: React.FC<PushNotificationManagerProps> = ({ className = '' }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Безопасное получение темы
  let theme = 'light';
  try {
    const themeContext = useTheme();
    theme = themeContext?.theme || 'light';
  } catch (error) {
    console.warn('Theme context not available, using light theme');
    theme = 'light';
  }
  
  // Дополнительная проверка поддержки браузера
  const isBrowserSupported = typeof window !== 'undefined' && 
                            typeof navigator !== 'undefined' &&
                            typeof document !== 'undefined';

  useEffect(() => {
    // Проверяем поддержку браузера перед инициализацией
    if (!isBrowserSupported) {
      setIsSupported(false);
      return;
    }
    
    // Ждём инициализации Firebase и только потом решаем, поддерживается ли
    // FCM (раньше проверка шла до init и всегда давала «нет»).
    let alive = true;
    universalNotificationService.ready().then(() => {
      if (!alive) return;
      try {
        checkSupport();
      } catch (error) {
        console.warn('Error in PushNotificationManager useEffect:', error);
        setIsSupported(false);
      }
    });
    return () => { alive = false; };
  }, [isBrowserSupported]);

  // Отдельный useEffect для проверки статуса подписки
  useEffect(() => {
    if (isSupported) {
      // Добавляем задержку для безопасной проверки
      const timer = setTimeout(() => {
        try {
          checkSubscriptionStatus();
        } catch (error) {
          console.warn('Error checking subscription status:', error);
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isSupported]);

  const checkSupport = () => {
    // Дополнительная проверка поддержки браузера
    if (!isBrowserSupported) {
      setIsSupported(false);
      return;
    }
    
    try {
      const supported = universalNotificationService.isSupported();
      const support = universalNotificationService.getSupport();
      const recommendedMethod = universalNotificationService.getRecommendedMethod();
      const platform = universalNotificationService.getPlatformInfo();
      
      try {
        console.log('Universal notification support check:', {
          supported,
          support,
          recommendedMethod,
          platform,
          userAgent: navigator?.userAgent || 'unknown'
        });
      } catch (error) {
        console.warn('Error logging support check:', error);
      }
      
      setIsSupported(supported);
    } catch (error) {
      console.warn('Error checking notification support:', error);
      setIsSupported(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    if (!isSupported) {
      return;
    }
    
    try {
      const status = await universalNotificationService.getSubscriptionStatus();
      setIsSubscribed(status);
    } catch (error) {
      console.warn('Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isSupported) {
      toast.error('Push-уведомления не поддерживаются в вашем браузере');
      return;
    }

    setIsLoading(true);
    try {
      // Подписываемся на уведомления (автоматически выбирает лучший метод)
      const success = await universalNotificationService.subscribe();
      
      if (success) {
        setIsSubscribed(true);
        toast.success('Уведомления включены!');
      } else {
        toast.error('Не удалось подписаться на уведомления');
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      toast.error('Ошибка при включении уведомлений');
      setIsSubscribed(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    try {
      const success = await universalNotificationService.unsubscribe();
      
      if (success) {
        setIsSubscribed(false);
        toast.success('Уведомления отключены');
      } else {
        toast.error('Не удалось отписаться от уведомлений');
        // Не меняем состояние, так как не знаем реальный статус
      }
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      toast.error('Ошибка при отключении уведомлений');
      // Не меняем состояние, так как не знаем реальный статус
    } finally {
      setIsLoading(false);
    }
  };


  // Дополнительная проверка поддержки браузера
  if (!isBrowserSupported) {
    console.log('Browser not supported, returning null');
    return null;
  }

  if (!isSupported) {
    console.log('Push notifications not supported, returning null');
    return null; // Не показываем компонент если не поддерживается
  }

  return (
    <div className={`${className}`}>
      {isSubscribed ? (
        <button
          onClick={handleUnsubscribe}
          disabled={isLoading}
          className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-red-600 hover:bg-red-700 text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Отключить push-уведомления"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <BellSlashIcon className="h-5 w-5" />
          )}
        </button>
      ) : (
        <button
          onClick={handleSubscribe}
          disabled={isLoading}
          className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'bg-primary-600 hover:bg-primary-700 text-white'
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Включить push-уведомления"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <BellIcon className="h-5 w-5" />
          )}
        </button>
      )}
    </div>
  );
};

export default PushNotificationManager;
